import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Can from 'components/Can';
import Feature from 'components/Feature';

import {
  Box,
  Card,
  Button,
  IconButton,
  CardHeader,
  CardActions,
  CardContent,
} from '@material-ui/core';
import clsx from 'clsx';
import i18n from 'i18n-js';
import useStyles from './Card.styled';
import { VerticalMenu } from 'components/UI';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useDispatch, useSelector } from 'react-redux';
import ArrowDropUp from '@material-ui/icons/ArrowDropUp';
import LinearProgress from '@material-ui/core/LinearProgress';
import { UserSettingsActions } from 'state/modules/userSettings';

const CollapsibleButton = ({ handleCollapse, collapsed }) => {
  const classes = useStyles();
  return (
    <IconButton
      data-cy="menu-btn"
      variant="contained"
      aria-haspopup="true"
      onClick={handleCollapse}
      aria-controls="collapsed-menu"
    >
      <ArrowDropUp className={clsx(classes.icon, collapsed && classes.collapsedIcon)} />
    </IconButton>
  );
};

CollapsibleButton.propTypes = {
  handleCollapse: PropTypes.func,
  collapsed: PropTypes.bool,
};

const BaseCard = ({
  id,
  title,
  onNext,
  datacy,
  loading,
  onEnter,
  children,
  onCancel,
  subtitle,
  onRefresh,
  menuItems,
  subheader,
  hideHeader,
  enableCollapse,
  hasEnterButton,
  hasCancelButton,
  menuPermissions,
  headerActionType,
  actionButtonLabel,
  contentRootClasses,
  cardProps,
  ...props
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const { collapsed } = useSelector((state) => state.userSettings);
  const [isCollapsed, setIsCollapsed] = useState(collapsed?.[id] || false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const displayVerticalMenu = () => {
    return (
      menuItems.length > 0 &&
      menuItems?.filter((el) => el.hasOwnProperty('display') && !el?.display)?.length !==
        menuItems.length
    );
  };

  const handleCollapse = () => {
    setIsCollapsed(!isCollapsed);
    dispatch(UserSettingsActions.handleCollapseCard({ id, collapsed: !isCollapsed }));
  };

  const renderHeaderAction = () => {
    let menu;
    switch (headerActionType) {
      case 'vertical':
        if (menuItems.length > 0 && displayVerticalMenu()) {
          menu = (
            <IconButton
              variant="contained"
              aria-haspopup="true"
              onClick={handleClick}
              data-cy="vertical-menu-btn"
              aria-controls="customized-menu"
            >
              <MoreVertIcon />
            </IconButton>
          );
        }
        break;

      case 'button':
        menu = (
          <Button
            color="primary"
            onClick={onNext}
            variant="contained"
            className={classes.actionButton}
          >
            {actionButtonLabel}
          </Button>
        );
        break;
      default:
        return null;
    }

    return (
      <Box>
        {onRefresh && !isCollapsed && (
          <Button
            color="primary"
            onClick={onRefresh}
            aria-haspopup="true"
            data-cy="refresh-btn"
            aria-controls="customized-menu"
            className={classes.refreshButton}
          >
            {i18n.t('common.refresh')}
          </Button>
        )}
        {enableCollapse && (
          <CollapsibleButton collapsed={isCollapsed} handleCollapse={handleCollapse} />
        )}
        {menu}
      </Box>
    );
  };

  const renderVerticalMenu = () => {
    if (!menuItems || !menuItems.length) return null;
    return <VerticalMenu items={menuItems} anchorEl={anchorEl} handleClose={handleClose} />;
  };

  return (
    <Card className={classes.root} data-cy={datacy} {...cardProps}>
      {!hideHeader && (
        <CardHeader
          title={title}
          subheader={subtitle}
          className={classes.headerRoot}
          action={renderHeaderAction()}
          titleTypographyProps={{ variant: 'h2', component: 'h2' }}
          {...props}
        />
      )}
      {headerActionType === 'vertical' && displayVerticalMenu() && (
        <>
          {menuPermissions?.type === 'can' ? (
            <Can I={menuPermissions.action} on={menuPermissions.subject}>
              {renderVerticalMenu()}
            </Can>
          ) : menuPermissions?.type === 'feature' ? (
            <Feature name={menuPermissions?.action} is={menuPermissions?.subject}>
              {renderVerticalMenu()}
            </Feature>
          ) : (
            <>{renderVerticalMenu()}</>
          )}
        </>
      )}

      {subheader && !collapsed?.[id] && <Box className={classes.subheader}>{subheader}</Box>}

      {collapsed?.[id] ? null : loading ? (
        <LinearProgress />
      ) : (
        <div style={{ height: '4px', overflow: 'hidden', position: 'relative' }} />
      )}

      <CardContent
        className={clsx(classes.content, { [classes.collapsed]: collapsed?.[id] })}
        classes={contentRootClasses || ''}
      >
        {children}
      </CardContent>

      {(hasCancelButton || hasEnterButton) && (
        <CardActions
          className={classes.footer}
          style={{
            justifyContent: `${hasCancelButton && hasEnterButton ? 'space-around' : 'flex-end'}`,
          }}
        >
          {hasCancelButton && (
            <Button variant="outlined" onClick={onCancel} color="primary">
              Cancel
            </Button>
          )}
          {hasEnterButton && (
            <Button variant="contained" onClick={onEnter} color="primary">
              Continue
            </Button>
          )}
        </CardActions>
      )}
    </Card>
  );
};

BaseCard.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  datacy: PropTypes.string,
  onNext: PropTypes.func,
  onEnter: PropTypes.func,
  onCancel: PropTypes.func,
  onRefresh: PropTypes.func,
  menuItems: PropTypes.array,
  enableCollapse: PropTypes.bool,
  menuPermissions: PropTypes.object,
  headerActionType: PropTypes.string,
  actionButtonLabel: PropTypes.string,
  contentRootClasses: PropTypes.object,
  loading: PropTypes.bool,
  children: PropTypes.node,
};

BaseCard.defaultProps = {
  menuItems: [],
  loading: false,
  onNext: () => {},
  onEnter: () => {},
  onCancel: () => {},
  menuPermissions: {},
  headerActionType: '',
  enableCollapse: false,
  contentRootClasses: {},
  id: `card-${Math.random().toString(36).slice(2)}`,
};

export default BaseCard;
